import { createSelector } from "reselect";
import {
  getObbBettingValidations,
  getObbBettingState,
} from "@ppb/tbd-store/state/betting/obb-betting/obb-betting-selectors";
import { getShowMaxPayoutNotification } from "../../store/state/betslip-module/betslip-module-selectors";
import { AlertProps, AlertViewModel } from "@ppb/the-wall-common/types";
import {
  ObbBetslipValidations,
  ObbBettingState,
  ObbPotentialBet,
  ObbPotentialBetsValidations,
  ObbValidations,
  ObbValidationSeverities,
  ObbValidationTypes,
} from "@ppb/tbd-store/state/betting/obb-betting/ObbBetting.types";
import { ApplicationState } from "@ppb/tbd-store/state/ApplicationState.types";
import type { BetslipLocalization, DailyPayoutLimitCapability } from "../../contract";
import { isTerritoryApplicableValidation, hasSpecialValidation } from "../../helpers/obb-territory-rules";
import {
  getAboveMaxPayoutGroupErrorNotification,
  getAboveMaxPayoutGroupWarningNotification,
  getAboveMaxPayoutNotification,
  getAboveMaxStakeNotification,
  getBelowMinStakeNotification,
  getIncrementOutOfRangeNotification,
  getMaxPayoutNotification,
  isMaxPayoutNotificationSupported,
} from "../../helpers/notifier-helper";
import { asHostProvidedKey } from "../../helpers/i18n";

export enum NOTIFICATION_TYPES {
  MAX_PAYOUT = "MAX_PAYOUT",
}

const VALIDATIONS_GROUPING = [[ObbValidationTypes.ABOVE_MAX_PAYOUT, ObbValidationTypes.ABOVE_MAX_STAKE]];

export type ObbValidation = {
  type: ObbValidationTypes | NOTIFICATION_TYPES;
  notification: AlertViewModel | null;
};

type PotentialBetValidation = {
  id: string;
  validation: ObbPotentialBetsValidations;
};

type ValidationsByTypeMap = {
  [k in ObbValidationTypes]?: PotentialBetValidation;
};

export type ObbNotifierContract = {
  localization: BetslipLocalization;
  maxPayoutTermsUrl: string;
  dailyPayoutLimit: DailyPayoutLimitCapability;
};

export const createNotificationFromPotentialBetValidation = (
  validation: ObbPotentialBetsValidations,
  localization: BetslipLocalization,
  potentialBet: ObbPotentialBet,
): AlertProps | null => {
  switch (validation.type) {
    case ObbValidationTypes.BELOW_MIN_STAKE: {
      return getBelowMinStakeNotification(validation.data.min, localization);
    }
    case ObbValidationTypes.ABOVE_MAX_STAKE: {
      return getAboveMaxStakeNotification(validation.data.max, localization);
    }
    case ObbValidationTypes.ABOVE_MAX_PAYOUT: {
      return getAboveMaxPayoutNotification(validation.data.max, localization);
    }
    case ObbValidationTypes.INCREMENT_OUT_OF_RANGE: {
      if (!potentialBet.minStakeIncrement) {
        return null;
      }
      return getIncrementOutOfRangeNotification(potentialBet.minStakeIncrement, localization);
    }
    default:
      return null;
  }
};

export const createNotificationFromBetslipValidation = (
  validation: ObbBetslipValidations,
  localization: BetslipLocalization,
  maxPayoutTermsUrl: string,
  dailyPayoutLimit: DailyPayoutLimitCapability,
): AlertProps | null => {
  const maxPayoutKey = dailyPayoutLimit.enabled ? asHostProvidedKey(dailyPayoutLimit.maxPayoutDisplayKey) : undefined;

  switch (validation.type) {
    case ObbValidationTypes.ABOVE_MAX_PAYOUT: {
      if (validation.severity === ObbValidationSeverities.ERROR && maxPayoutKey) {
        return getAboveMaxPayoutGroupErrorNotification(localization, maxPayoutKey);
      }

      if (validation.severity === ObbValidationSeverities.WARNING && maxPayoutKey) {
        return getAboveMaxPayoutGroupWarningNotification(localization, maxPayoutKey, maxPayoutTermsUrl);
      }

      return null;
    }
    default:
      return null;
  }
};

export const buildObbValidation = (
  potentialBetValidation: PotentialBetValidation,
  notification: AlertViewModel | null,
): ObbValidation => {
  if (!notification?.items) {
    return {
      type: potentialBetValidation.validation.type,
      notification: null,
    };
  }

  return {
    type: potentialBetValidation.validation.type,
    notification: {
      id: potentialBetValidation.id,
      ...notification,
    },
  };
};

export const mergeObbValidation = (validationA: ObbValidation, validationB: ObbValidation): ObbValidation => {
  const { notification: notificationA } = validationA;
  const { notification: notificationB } = validationB;

  if (!notificationA || !notificationB) {
    return {
      ...validationA,
      ...validationB,
      notification: null,
    };
  }

  return {
    ...validationA,
    ...validationB,
    notification: {
      ...notificationA,
      ...notificationB,
      items: notificationA.items && notificationB.items && [...notificationA.items, ...notificationB.items],
    },
  };
};

const groupPotentialBetValidations = (potentialBetValidations: ValidationsByTypeMap): PotentialBetValidation[][] => {
  const localPotentialBetValidations = { ...potentialBetValidations };
  const groupedValidations = VALIDATIONS_GROUPING.map((validationGroup) =>
    validationGroup
      .map((type) => {
        const validation = localPotentialBetValidations[type];

        delete localPotentialBetValidations[type]; // eslint-disable-line @typescript-eslint/no-dynamic-delete

        return validation;
      })
      .filter((validation): validation is PotentialBetValidation => !!validation),
  );

  return [...groupedValidations, ...Object.values(localPotentialBetValidations).map((validation) => [validation])];
};

const buildUniquePotentialBetValidationsByTypeMap = (
  potentialBetsValidations: ObbValidations["potentialBets"],
  localization: BetslipLocalization,
): ValidationsByTypeMap =>
  Object.keys(potentialBetsValidations).reduce<ValidationsByTypeMap>((acc, potentialBetId) => {
    const currentPotentialBetValidations = potentialBetsValidations[potentialBetId];
    currentPotentialBetValidations.forEach((validation) => {
      if (isTerritoryApplicableValidation(validation.type, localization)) {
        acc[validation.type] = { id: potentialBetId, validation };
      }
    });
    return acc;
  }, {});

const buildBetslipValidations = (
  coreBetslipValidations: ObbBetslipValidations[],
  localization: BetslipLocalization,
  maxPayoutTermsUrl: string,
  hasAboveMaxStakeValidation: boolean,
  dailyPayoutLimit: DailyPayoutLimitCapability,
): ObbValidation[] =>
  coreBetslipValidations.reduce<ObbValidation[]>((allObbValidations, currentBetslipValidation) => {
    if (!hasSpecialValidation(currentBetslipValidation.type, localization, hasAboveMaxStakeValidation)) {
      return allObbValidations;
    }
    const obbValidation = {
      type: currentBetslipValidation.type,
      notification: createNotificationFromBetslipValidation(
        currentBetslipValidation,
        localization,
        maxPayoutTermsUrl,
        dailyPayoutLimit,
      ),
    };
    allObbValidations.push(obbValidation);
    return allObbValidations;
  }, []);

const flattenPotentialBetsValidations = (
  potentialBetValidations: PotentialBetValidation[],
  localization: BetslipLocalization,
  obbBettingState: ObbBettingState,
): ObbValidation => {
  const [firstPotentialBetValidation, ...remainingPotentialBetValidations] = potentialBetValidations;
  const firstPotentialBet = obbBettingState.potentialBets[firstPotentialBetValidation.id];
  const firstNotification = createNotificationFromPotentialBetValidation(
    firstPotentialBetValidation.validation,
    localization,
    firstPotentialBet,
  );

  const flattenedValidation = remainingPotentialBetValidations.reduce<ObbValidation>(
    (flatValidation, validationGroup) => {
      const potentialBet = obbBettingState.potentialBets[validationGroup.id];
      const notification = createNotificationFromPotentialBetValidation(
        validationGroup.validation,
        localization,
        potentialBet,
      );
      const obbValidation = buildObbValidation(validationGroup, notification);

      return mergeObbValidation(flatValidation, obbValidation);
    },
    buildObbValidation(firstPotentialBetValidation, firstNotification),
  );

  return flattenedValidation;
};

export const createValidationsSelector = () =>
  createSelector(
    [
      getObbBettingValidations,
      getObbBettingState,
      getShowMaxPayoutNotification,
      (_state: ApplicationState, contract: ObbNotifierContract) => contract.localization,
      (_state: ApplicationState, contract: ObbNotifierContract) => contract.maxPayoutTermsUrl,
      (_state: ApplicationState, contract: ObbNotifierContract) => contract.dailyPayoutLimit,
    ],
    (
      obbValidations,
      obbBettingState,
      isMaxPayoutNotificationVisible,
      localization,
      maxPayoutTermsUrl,
      dailyPayoutLimit,
    ) => {
      if (!localization.jurisdiction) {
        return [];
      }

      const uniquePotentialBetValidations = buildUniquePotentialBetValidationsByTypeMap(
        obbValidations.potentialBets,
        localization,
      );

      const groupedPotentialBetValidation = groupPotentialBetValidations(uniquePotentialBetValidations);
      const obbPotentialBetValidations = groupedPotentialBetValidation.reduce<ObbValidation[]>(
        (allFlattenedPotentialBetValidations, potentialBetValidations) => {
          if (potentialBetValidations.length) {
            allFlattenedPotentialBetValidations.push(
              flattenPotentialBetsValidations(potentialBetValidations, localization, obbBettingState),
            );
          }
          return allFlattenedPotentialBetValidations;
        },
        [],
      );

      const hasAboveMaxStakeValidation = obbPotentialBetValidations.some(
        (validation) => validation.type === ObbValidationTypes.ABOVE_MAX_STAKE,
      );

      const obbBetslipValidations = buildBetslipValidations(
        obbValidations.betslip,
        localization,
        maxPayoutTermsUrl,
        hasAboveMaxStakeValidation,
        dailyPayoutLimit,
      );

      const showMaxPayoutNotification =
        isMaxPayoutNotificationVisible && isMaxPayoutNotificationSupported(localization);
      const maxPayoutNotification = showMaxPayoutNotification
        ? [
            {
              type: NOTIFICATION_TYPES.MAX_PAYOUT,
              notification: getMaxPayoutNotification(maxPayoutTermsUrl),
            },
          ]
        : [];

      // Uniques, Groups & Flattens all validations into notifications
      return [...obbPotentialBetValidations, ...obbBetslipValidations, ...maxPayoutNotification];
    },
  );
